import {
    getInitialState,
    buildRequestBody,
    computeAdoptionTimeRangeStartAndEnd,
    computeAdoptionTimeSeries,
    computePreviousAdoptionTimeSeries,
    buildAdoptionAggregations,
    transformAggregationResults,
    buildAppData,
    splitCountableIdsByKind,
} from './adoption-trends.utils.js';
import { runAggregationOnce } from '@/utils/aggregation';
import { hasSubscriptionFlag } from '@/data/flags/local-flags';
import { usePagesStore } from '@/stores/pages.store';
import { getAppId } from '@/vuex/modules/trends/trends-settings.module.js';

const adoptionTrends = {
    namespaced: true,
    state: getInitialState(),
    mutations: {
        setAdoptionInsights (state, { insights }) {
            state.insights = insights;
        },
        setInsightRequestBody (state, { requestBody }) {
            state.insightRequestBody = requestBody;
        },
        setLoading (state, { isLoading }) {
            state.isLoading = isLoading;
        },
        setError (state, { error }) {
            state.error = error;
        },
    },
    actions: {
        async fetchAdoptionInsights ({ commit, rootGetters, dispatch }) {
            commit('setLoading', { isLoading: true });
            commit('setError', { error: null });

            try {
                const appId = await getAppId(rootGetters);

                await Promise.all([
                    usePagesStore().loadAll(),
                    dispatch('features/loadAll', {}, { root: true }),
                ]);
                const countableIds = rootGetters['trendsSettings/getEvents']?.map((event) => ({
                    id: event.id,
                    kind: event.kind,
                }));
                const includeAnonymous = hasSubscriptionFlag('showAnonymous');
                const currentTimeSeries = computeAdoptionTimeSeries(7);
                const timeRangeStartAndEnd = computeAdoptionTimeRangeStartAndEnd(7);
                const previousTimeSeries = computePreviousAdoptionTimeSeries(currentTimeSeries);
                const requestBody = buildRequestBody(appId, countableIds, includeAnonymous, {
                    currentTimeSeries,
                    previousTimeSeries,
                    timeRangeStart: timeRangeStartAndEnd.start,
                    timeRangeEnd: timeRangeStartAndEnd.end,
                });
                commit('setInsightRequestBody', { requestBody });

                const groupId = rootGetters['trendsSettings/getGroupId'] ?? undefined;
                const { featureIds, pageIds, trackEventIds } = splitCountableIdsByKind(countableIds ?? []);
                const aggregations = buildAdoptionAggregations({
                    appId,
                    groupId,
                    hasShowAnonymousFlag: includeAnonymous,
                    featureIds,
                    pageIds,
                    trackEventIds,
                });

                const rawResults = await Promise.all(
                    aggregations.map((agg) => runAggregationOnce(agg)),
                );

                const appData = buildAppData(rootGetters, appId);
                const hasSelectedEvents = (countableIds?.length ?? 0) > 0;
                const insights = transformAggregationResults(
                    aggregations,
                    rawResults,
                    appData,
                    hasSelectedEvents,
                );

                commit('setAdoptionInsights', { insights });

                return insights;
            } catch (error) {
                commit('setError', { error: error.message || 'Failed to fetch adoption insights' });
                throw error;
            } finally {
                commit('setLoading', { isLoading: false });
            }
        },
    },
    getters: {
        adoptionInsights: (state) => state.insights || [],
        insightRequestBody: (state) => state.insightRequestBody,
        isLoading: (state) => state.isLoading,
        hasError: (state) => !!state.error,
        error: (state) => state.error,
    },
};

export default adoptionTrends;
